"use client";

import type { HeroesBlock, Media } from "@mw/shared/payload-types";
import type { MultiTemplateField } from "@utilities/types";
import type { ReactNode } from "react";

import NextImage from "next/image";
import { clsx } from "clsx";
import { ModalVideoPlayer } from "@ui/components/core/ModalVideoPlayer";
import { Button } from "@ui/components/core/Button";
import { isEntity } from "@/app/_utilities/payload";
import { getImageProps } from "@utilities/payload";
import { sizes } from "@ui/lib/sizes";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";

type SplitConfig = NonNullable<HeroesBlock["heroConfiguration"]["split"]>;
export type SplitMedia = MultiTemplateField<SplitConfig["media"]>;

export function HeroSplitMedia({
  media,
  hasBorder,
  watchButtonIcon,
}: {
  media: SplitMedia;
  hasBorder: boolean;
  watchButtonIcon?: ReactNode;
}) {
  const roundedClass = hasBorder ? undefined : "tw-rounded-2xl";
  switch (media.template) {
    case "image": {
      const imageMedia = isEntity(media.image.image) ? (media.image.image as Media) : undefined;
      const imageProps = getImageProps(imageMedia, { placeholder: true, fill: true });
      return (
        <div className={clsx("tw-relative tw-aspect-square tw-overflow-hidden", roundedClass)}>
          {imageProps && (
            <NextImage {...imageProps} className='tw-object-cover' priority sizes={sizes("100vw", { lg: "50vw" })} />
          )}
        </div>
      );
    }
    case "video": {
      const videoMedia = isEntity(media.video.video) ? (media.video.video as Media) : undefined;
      if (!videoMedia?.url) return null;
      const watchText = media.video.watchButtonText;
      const hasWatchButton = watchText || watchButtonIcon;
      return (
        <div className={clsx("tw-relative tw-aspect-square tw-overflow-hidden", roundedClass)}>
          <video
            autoPlay
            muted
            loop
            playsInline
            aria-hidden='true'
            src={videoMedia.url}
            className='tw-absolute tw-inset-0 tw-size-full tw-object-cover'
          />
          {hasWatchButton && (
            <div className='tw-absolute tw-bottom-4 tw-right-4 tw-z-10'>
              <ModalVideoPlayer
                video={videoMedia.url}
                hideDuration
                customTrigger={
                  <Button
                    size='lg'
                    variant='elevated'
                    iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
                    aria-label={!watchText ? "Watch video" : undefined}
                    icon={watchButtonIcon}
                  >
                    {watchText}
                  </Button>
                }
              />
            </div>
          )}
        </div>
      );
    }
  }
}
